import React from "react";
import { useNavigate } from "react-router-dom";
import { RevealHeading, StaggerReveal, MagneticButton } from "./Motion";
import { Sparkle, BnGlyph, ScribbleCircle } from "./Decorations";
import { Check } from "lucide-react";

const TIERS = [
    {
        name: "Free",
        bdt: "0",
        usd: "Free forever",
        tagline: "Try it. No card.",
        features: [
            "5,000 chars / month (~5 articles)",
            "2 voices (Noor & Rajib)",
            "Standard TTS engine",
            "3-day audio retention",
            "Audo watermark on player",
            "Community support",
        ],
        cta: "Start free",
        highlight: false,
        color: "#3D5BFF",
    },
    {
        name: "Starter",
        bdt: "499",
        usd: "≈ $4.50",
        tagline: "Small portals & blogs.",
        features: [
            "40,000 chars / month",
            "8 voices",
            "Standard TTS engine",
            "7-day audio retention",
            "Basic analytics",
            "Email support (48h)",
        ],
        cta: "Get Starter",
        highlight: false,
        color: "#7B3FE4",
    },
    {
        name: "Pro",
        bdt: "1,999",
        usd: "≈ $18",
        tagline: "Most newsrooms pick this.",
        features: [
            "200,000 chars / month",
            "All voices — premium HD",
            "✦ HD Audio · Gemini 3.1 Flash TTS",
            "30-day audio retention",
            "Full analytics + heatmaps",
            "WordPress plugin",
            "70/30 audio ad revenue share",
            "Email + WhatsApp support",
        ],
        cta: "Go Pro",
        highlight: true,
        color: "#FF5B3E",
        badge: "Most Popular",
    },
    {
        name: "Business",
        bdt: "4,999",
        usd: "≈ $45",
        tagline: "Tier-1 portals.",
        features: [
            "560,000 chars / month",
            "All voices + 1 custom voice",
            "✦ HD Audio · Gemini 3.1 Flash TTS",
            "Unlimited audio retention",
            "White-label embed player",
            "API + priority queue",
            "Analytics export API",
            "Phone / Slack / WhatsApp (4h SLA)",
        ],
        cta: "Get Business",
        highlight: false,
        color: "#2EE8B5",
    },
];

const Card = ({ t, onSelect }) => (
    <article
        data-stagger
        data-testid={`pricing-${t.name.toLowerCase()}`}
        className={`relative rounded-3xl p-7 transition-all duration-300 hover:-translate-y-2 flex flex-col h-full ${
            t.highlight
                ? "bg-white border-2 border-coral shadow-coral"
                : "bg-white border border-cream-300 shadow-soft hover:shadow-soft-lg"
        }`}
    >
        {t.badge && (
            <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-coral text-white text-[11px] font-bold uppercase tracking-[0.18em] px-3 py-1 rounded-full whitespace-nowrap shadow-coral">
                {t.badge}
            </span>
        )}
        <div className="flex items-center gap-2 mb-3">
            <span className="w-2 h-2 rounded-full" style={{ background: t.color }}/>
            <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-muted">{t.name}</span>
        </div>
        <p className="text-[13px] text-ink-muted italic mb-5">{t.tagline}</p>
        <div className="flex items-baseline gap-2 mb-1">
            <span className="font-display text-[14px] text-ink-muted">৳</span>
            <span className="font-display text-[52px] font-bold leading-none tracking-tight text-ink">{t.bdt}</span>
            <span className="text-[13px] text-ink-muted">/ mo</span>
        </div>
        <p className="text-[11px] text-ink-muted mb-7">{t.usd}</p>
        <ul className="space-y-3 mb-7 flex-1">
            {t.features.map((f, i) => (
                <li key={i} className="flex items-start gap-2.5 text-[14px] text-ink">
                    <span className="mt-0.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-coral flex-shrink-0">
                        <Check size={10} className="text-white" strokeWidth={3.5}/>
                    </span>
                    {f}
                </li>
            ))}
        </ul>
        <button
            onClick={() => onSelect(t)}
            className={`w-full rounded-full py-3.5 text-[14px] font-semibold transition-all mt-auto ${
                t.highlight
                    ? "bg-ink text-cream-100 hover:bg-coral hover:shadow-coral"
                    : "bg-cream-100 text-ink hover:bg-ink hover:text-cream-100"
            }`}
        >
            {t.cta}
        </button>
    </article>
);

export const Pricing = () => {
    const navigate = useNavigate();

    const goCreateAccount = () => {
        navigate("/register");
    };

    return (
        <section id="pricing" className="relative py-24 lg:py-32 overflow-hidden">
            <ScribbleCircle color="#FFD23F" className="absolute top-20 right-[5%] w-28 h-28 opacity-50 animate-float-slow"/>
            <BnGlyph glyph="৳" className="absolute -left-2 top-32 text-[180px] lg:text-[280px] leading-none font-bold opacity-[0.05] select-none" color="#FF5B3E"/>

            <div className="relative mx-auto max-w-[1240px] px-5 sm:px-8">
                <div className="text-center mb-14">
                    <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-coral mb-5">Pricing</p>
                    <RevealHeading as="h2" className="h-display text-[44px] sm:text-[64px] lg:text-[88px] text-ink">
                        Pricing that makes sense in <span className="italic-display text-coral">BDT.</span>
                    </RevealHeading>
                    <p className="mt-6 max-w-[560px] mx-auto text-[16px] text-ink-muted">
                        Built for Bangladeshi margins. No surprise USD invoices. Cancel anytime.
                    </p>
                </div>

                <StaggerReveal className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6 relative items-stretch">
                    {TIERS.map((t, i) => (
                        <div key={i} className="relative flex flex-col">
                            <Card t={t} onSelect={goCreateAccount}/>
                            {t.highlight && <Sparkle color="#FFD23F" className="absolute -top-6 -right-6 w-9 h-9 animate-float-fast"/>}
                        </div>
                    ))}
                </StaggerReveal>

                {/* Enterprise card */}
                <div data-testid="pricing-enterprise" className="mt-6 lg:mt-8 bg-ink-pill text-cream-100 rounded-3xl p-7 lg:p-10 flex flex-col lg:flex-row items-center justify-between gap-6 shadow-soft-lg">
                    <div>
                        <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-coral">Enterprise</span>
                        <h3 className="mt-2 font-display text-[28px] lg:text-[40px] font-semibold tracking-tight">
                            Running a national network? <span className="italic-display text-coral">Let's talk.</span>
                        </h3>
                        <p className="mt-2 text-[14px] text-cream-100/70 max-w-xl">Custom voices, on-prem deployment, white-label, SLA, training. Volume discounts after 50K articles/mo.</p>
                    </div>
                    <MagneticButton
                        onClick={goCreateAccount}
                        className="bg-cream-100 text-ink rounded-full px-7 py-4 text-[14px] font-semibold whitespace-nowrap hover:bg-coral hover:text-white transition-colors"
                    >
                        Talk to us →
                    </MagneticButton>
                </div>
            </div>
        </section>
    );
};
